/**
 * V1e · Modal de modération d'une CredentialVerification.
 *
 * Affiche :
 *   - Métadonnées de la demande (type, fieldRef, soumissionnaire, dates)
 *   - Snapshot JSON du champ (school/degree pour DIPLOMA, etc.)
 *   - Preview de la pièce justificative :
 *       * PDF → iframe (Chrome rend en natif si bucket public)
 *       * Image → <img> direct
 *   - Form de décision : Approve / Mark Under Review / Reject
 *   - Champs reviewNote (commun) + rejectReason (requis si REJECT)
 *
 * Workflow attendu pour le modérateur :
 *   1. Click "Modérer" sur une ligne de la queue
 *   2. Examine la pièce dans l'iframe
 *   3. Compare avec fieldSnapshot (ex: école/diplôme indiqués)
 *   4. Décision + note → submit
 */

import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useNotify } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  CircularProgress,
  Alert,
  Divider,
  Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { REVIEW_CREDENTIAL_VERIFICATION } from './credentials.queries';
import {
  CredentialReviewDecision,
  CredentialVerification,
  CredentialVerificationStatus,
} from './credentials.types';

interface ReviewModalProps {
  verification: CredentialVerification | null;
  open: boolean;
  onClose: () => void;
  onReviewed: () => void;
}

/**
 * V1e · Détecte le type MIME pour choisir la stratégie de preview.
 * - `application/pdf` → iframe (zoom natif, scroll)
 * - `image/*` → <img> avec max-width
 * - autre / null → lien "Ouvrir dans un nouvel onglet" en fallback
 */
function detectPreviewKind(mimeType: string | null, url: string | null): 'pdf' | 'image' | 'link' {
  if (!url) return 'link';
  if (mimeType?.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  // Fallback : deviner par extension URL si MIME absent
  if (/\.(jpg|jpeg|png|webp|gif)$/i.test(url)) return 'image';
  if (/\.pdf$/i.test(url)) return 'pdf';
  return 'link';
}

export function CredentialReviewModal({
  verification,
  open,
  onClose,
  onReviewed,
}: ReviewModalProps) {
  const notify = useNotify();
  const [decision, setDecision] = useState<CredentialReviewDecision>(
    CredentialReviewDecision.APPROVED,
  );
  const [reviewNote, setReviewNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  const [reviewMutation, { loading }] = useMutation(REVIEW_CREDENTIAL_VERIFICATION);

  // Reset form state when modal opens/closes or verification changes
  React.useEffect(() => {
    if (open && verification) {
      setDecision(CredentialReviewDecision.APPROVED);
      setReviewNote('');
      setRejectReason('');
    }
  }, [open, verification]);

  if (!verification) return null;

  const previewKind = detectPreviewKind(
    verification.documentMimeType,
    verification.documentUrl,
  );

  const isAlreadyFinal =
    verification.status === CredentialVerificationStatus.APPROVED ||
    verification.status === CredentialVerificationStatus.REJECTED;

  const handleSubmit = async () => {
    // Validation côté UI : le service applicatif valide aussi ces règles
    if (decision === CredentialReviewDecision.REJECTED && !rejectReason.trim()) {
      notify('Une raison de refus est requise', { type: 'error' });
      return;
    }
    try {
      await reviewMutation({
        variables: {
          verificationId: verification.id,
          input: {
            decision,
            reviewNote: reviewNote.trim() || undefined,
            rejectReason: rejectReason.trim() || undefined,
          },
        },
      });
      notify(`Demande ${decision.toLowerCase()}`, { type: 'success' });
      onReviewed();
      onClose();
    } catch (error: any) {
      notify(`Erreur : ${error.message ?? 'inconnue'}`, { type: 'error' });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      data-testid="credential-review-modal"
    >
      <DialogTitle>
        Modération · {verification.type}
        <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
          fieldRef: <code>{verification.fieldRef}</code>
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {isAlreadyFinal && (
            <Alert severity="warning">
              Cette demande est déjà <strong>{verification.status}</strong>. Une
              re-modération est interdite — l'utilisateur doit re-soumettre une
              nouvelle demande pour relancer le workflow.
            </Alert>
          )}

          {/* Métadonnées */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Métadonnées de la demande
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={`Status: ${verification.status}`}
                color={
                  verification.status === 'APPROVED'
                    ? 'success'
                    : verification.status === 'REJECTED'
                      ? 'error'
                      : 'warning'
                }
                size="small"
              />
              <Chip
                label={`User: ${verification.userId.slice(0, 8)}…`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`Card: ${verification.digitalCardId.slice(0, 8)}…`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`Soumis: ${new Date(verification.submittedAt).toLocaleString('fr-FR')}`}
                size="small"
                variant="outlined"
              />
              {verification.documentSize && (
                <Chip
                  label={`Doc: ${(verification.documentSize / 1024 / 1024).toFixed(2)} MB`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>

          {/* Snapshot du champ */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Données déclarées par l'utilisateur (snapshot au submit)
            </Typography>
            <Box
              component="pre"
              data-testid="credential-review-snapshot"
              sx={{
                background: '#F4F4F5',
                p: 2,
                borderRadius: 1,
                fontSize: 12,
                overflow: 'auto',
                maxHeight: 200,
              }}
            >
              {JSON.stringify(verification.fieldSnapshot, null, 2)}
            </Box>
          </Box>

          {/* Note de soumission */}
          {verification.submissionNote && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Note du soumissionnaire
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  background: '#FBFBE9',
                  p: 2,
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {verification.submissionNote}
              </Typography>
            </Box>
          )}

          {/* Preview de la pièce */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Pièce justificative
            </Typography>
            {previewKind === 'pdf' && verification.documentUrl && (
              <iframe
                data-testid="credential-review-preview-pdf"
                src={verification.documentUrl}
                title="Pièce justificative PDF"
                style={{
                  width: '100%',
                  height: '500px',
                  border: '1px solid #E5E7EB',
                  borderRadius: 4,
                }}
              />
            )}
            {previewKind === 'image' && verification.documentUrl && (
              <Box
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: 1,
                  p: 1,
                  textAlign: 'center',
                }}
              >
                <img
                  data-testid="credential-review-preview-image"
                  src={verification.documentUrl}
                  alt="Pièce justificative"
                  style={{ maxWidth: '100%', maxHeight: 500 }}
                />
              </Box>
            )}
            {previewKind === 'link' && (
              <Alert severity="info">
                Format de prévisualisation non supporté.{' '}
                {verification.documentUrl && (
                  <Link
                    href={verification.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Ouvrir dans un nouvel onglet <OpenInNewIcon fontSize="small" />
                  </Link>
                )}
              </Alert>
            )}
          </Box>

          <Divider />

          {/* Form de décision */}
          {!isAlreadyFinal && (
            <FormControl component="fieldset">
              <FormLabel component="legend">Décision</FormLabel>
              <RadioGroup
                row
                value={decision}
                onChange={(e) =>
                  setDecision(e.target.value as CredentialReviewDecision)
                }
                data-testid="credential-review-decision-radio"
              >
                <FormControlLabel
                  value={CredentialReviewDecision.APPROVED}
                  control={<Radio />}
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CheckCircleIcon fontSize="small" sx={{ color: '#10B981' }} />
                      <span>Approuver</span>
                    </Stack>
                  }
                />
                <FormControlLabel
                  value={CredentialReviewDecision.UNDER_REVIEW}
                  control={<Radio />}
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <HourglassBottomIcon fontSize="small" sx={{ color: '#F59E0B' }} />
                      <span>Marquer "en cours d'examen"</span>
                    </Stack>
                  }
                />
                <FormControlLabel
                  value={CredentialReviewDecision.REJECTED}
                  control={<Radio />}
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CancelIcon fontSize="small" sx={{ color: '#EF4444' }} />
                      <span>Refuser</span>
                    </Stack>
                  }
                />
              </RadioGroup>
            </FormControl>
          )}

          {!isAlreadyFinal && (
            <TextField
              data-testid="credential-review-note-input"
              label="Note de modération (optionnelle, visible côté utilisateur)"
              multiline
              minRows={2}
              maxRows={4}
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 2000 }}
            />
          )}

          {!isAlreadyFinal && decision === CredentialReviewDecision.REJECTED && (
            <TextField
              data-testid="credential-review-reject-input"
              label="Raison du refus (obligatoire, visible côté utilisateur)"
              multiline
              minRows={2}
              maxRows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              fullWidth
              required
              inputProps={{ maxLength: 2000 }}
              error={!rejectReason.trim()}
              helperText={
                !rejectReason.trim()
                  ? 'Une raison est requise pour un refus.'
                  : undefined
              }
            />
          )}

          {/* Affichage des décisions précédentes pour contexte */}
          {isAlreadyFinal && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Décision précédente
              </Typography>
              {verification.reviewNote && (
                <Typography variant="body2">
                  <strong>Note :</strong> {verification.reviewNote}
                </Typography>
              )}
              {verification.rejectReason && (
                <Typography variant="body2" color="error">
                  <strong>Motif refus :</strong> {verification.rejectReason}
                </Typography>
              )}
              {verification.reviewedAt && (
                <Typography variant="caption" color="text.secondary">
                  Le {new Date(verification.reviewedAt).toLocaleString('fr-FR')}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} data-testid="credential-review-cancel">
          Annuler
        </Button>
        {!isAlreadyFinal && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color={
              decision === CredentialReviewDecision.APPROVED
                ? 'success'
                : decision === CredentialReviewDecision.REJECTED
                  ? 'error'
                  : 'primary'
            }
            disabled={
              loading ||
              (decision === CredentialReviewDecision.REJECTED && !rejectReason.trim())
            }
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
            data-testid="credential-review-submit"
          >
            {loading ? 'Soumission…' : 'Confirmer la décision'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
